import { Link, usePage } from "@inertiajs/react"
import { SeoHead } from "@/components/SeoHead"
import { organizationNode, websiteNode } from "@/lib/seo"
import type { PageProps } from "@/types/inertia"
import { MarketingLayout } from "@/components/marketing/MarketingLayout"
import { Section, Eyebrow } from "@/components/marketing/sections/Section"

type PostCard = {
  id: number
  title: string
  slug: string
  excerpt: string | null
  cover_image_url: string | null
  author: string | null
  published_at: string | null
}

const DESCRIPTION =
  "Insights on podcast strategy, authority-building, and turning conversations into trust — from the team behind FullCast."

export default function BlogIndex({ posts }: { posts: PostCard[] }) {
  const base = (usePage<PageProps>().props.app_url || "https://fullcast.co").replace(/\/$/, "")

  return (
    <MarketingLayout>
      <SeoHead
        title="Blog"
        description={DESCRIPTION}
        jsonLd={[
          organizationNode(base),
          websiteNode(base),
          {
            "@type": "Blog",
            "@id": `${base}/blog#blog`,
            name: "The FullCast Blog",
            url: `${base}/blog`,
            description: DESCRIPTION,
            publisher: { "@id": `${base}/#organization` },
          },
        ]}
      >
        <link rel="alternate" type="application/rss+xml" title="FullCast Blog" href="/blog/feed" />
      </SeoHead>

      <Section>
        <Eyebrow>Blog</Eyebrow>
        <h1 className="display-2 mt-4">Ideas on podcasting with purpose</h1>
        <p className="mt-5 max-w-2xl text-lg text-ink-body">
          Strategy, authority, and relationship-building — notes on making a
          podcast that earns trust with the right people.
        </p>

        {posts.length === 0 ? (
          <p className="mt-12 text-ink-muted">No posts yet. Check back soon.</p>
        ) : (
          <div className="mt-12 grid gap-8 sm:grid-cols-2">
            {posts.map((post) => (
              <article key={post.id} className="group">
                <Link href={`/blog/${post.slug}`} className="block no-underline">
                  <div className="aspect-[16/9] overflow-hidden rounded-xl border border-hairline bg-surface">
                    {post.cover_image_url ? (
                      <img
                        src={post.cover_image_url}
                        alt={post.title}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                        loading="lazy"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-ink-muted">
                        <span className="font-display text-sm">FullCast</span>
                      </div>
                    )}
                  </div>
                  <h3 className="mt-4 transition-colors group-hover:text-accent-display">
                    {post.title}
                  </h3>
                  {post.excerpt && (
                    <p className="mt-2 text-ink-muted">{post.excerpt}</p>
                  )}
                  {post.author && (
                    <p className="mt-3 text-sm text-ink-muted">{post.author}</p>
                  )}
                </Link>
              </article>
            ))}
          </div>
        )}
      </Section>
    </MarketingLayout>
  )
}
